import { normalizeEstimate } from "../pages/estimate/utils/normalizeEstimate";
import { calculateEstimateTotals, round2 } from "./calculateEstimateTotals";
import {
  getRowQuantity,
  getRowUnitPrice,
  isManualRow,
  isAiAdjusted,
  isReviewRow,
  normalizeRowMeta,
} from "./estimateRows";

function safeObject(value, fallback = {}) {
  if (!value) return fallback;

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      return typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
        ? parsed
        : fallback;
    } catch {
      return fallback;
    }
  }

  return fallback;
}

export function normalizeEstimateForEditor(rawEstimate) {
  const estimateObj = safeObject(rawEstimate, {});
  const normalized = normalizeEstimate(estimateObj);

  const rawMeta = safeObject(estimateObj.meta, {});
const normalizedMeta = safeObject(normalized.meta, {});
const enrichedParsedData = safeObject(rawMeta.enrichedParsedData, {});
const enrichedStructure = safeObject(enrichedParsedData.structure, {});
const enrichedRoof = safeObject(enrichedParsedData.roof, {});

const resolvedCalculator =
  normalizedMeta.calculator ||
  rawMeta.calculator ||
  rawMeta.parsedProjectType ||
  enrichedParsedData.primaryProjectType ||
  enrichedParsedData.projectType ||
  (Array.isArray(rawMeta.parsedProjectTypes) && rawMeta.parsedProjectTypes[0]) ||
  (Array.isArray(enrichedParsedData.projectTypes) &&
    enrichedParsedData.projectTypes[0]) ||
  null;

const resolvedStructure = {
  ...safeObject(rawMeta.structure, {}),
  ...enrichedStructure,
};

const resolvedRoof = {
  ...safeObject(rawMeta.roof, {}),
  ...enrichedRoof,
};

  const materials = Array.isArray(normalized.materials)
    ? normalized.materials.map((row, index) => ({
        ...row,
        qty: getRowQuantity(row),
        unitPrice: getRowUnitPrice(row),
        price: getRowUnitPrice(row),
        manuallyEdited: isManualRow(row),
        aiAdjusted: isAiAdjusted(row),
        meta: normalizeRowMeta(row, "material", index),
      }))
    : [];

  const labour = Array.isArray(normalized.labour)
    ? normalized.labour.map((row, index) => ({
        ...row,
        hours: getRowQuantity(row),
        hourlyRate: getRowUnitPrice(row),
        rate: getRowUnitPrice(row),
        manuallyEdited: isManualRow(row),
        aiAdjusted: isAiAdjusted(row),
        meta: normalizeRowMeta(row, "labour", index),
      }))
    : [];

  const extras = Array.isArray(normalized.extras)
    ? normalized.extras.map((row, index) => ({
        ...row,
        name: row?.name || row?.label || "Untitled extra",
        qty: getRowQuantity(row),
        price: getRowUnitPrice(row),
        unitPrice: getRowUnitPrice(row),
        manuallyEdited: isManualRow(row),
        aiAdjusted: isAiAdjusted(row),
        meta: normalizeRowMeta(row, "extra", index),
      }))
    : [];

  return {
    ...normalized,
    materials,
    labour,
    extras,
    meta: {
  ...rawMeta,
  ...normalizedMeta,
  calculator: resolvedCalculator,
  structure: resolvedStructure,
  roof: resolvedRoof,
},
    summary: safeObject(normalized.summary, {}),
  };
}

export function recalculateEstimate(estimate) {
  const normalized = normalizeEstimateForEditor(estimate || {});

  const materials = (normalized.materials || []).map((row, index) => {
    const qty = getRowQuantity(row);
    const unitPrice = getRowUnitPrice(row);

    return {
      ...row,
      quantity: qty,
      qty,
      unitPrice,
      price: unitPrice,
      total: round2(qty * unitPrice),
      flags: {
        ...(row.flags || {}),
        manuallyEdited: isManualRow(row),
        aiAdjusted: isAiAdjusted(row),
        reviewRequired: isReviewRow(row),
      },
      manuallyEdited: isManualRow(row),
      aiAdjusted: isAiAdjusted(row),
      meta: normalizeRowMeta(row, "material", index),
    };
  });

  const labour = (normalized.labour || []).map((row, index) => {
    const hours =
      row.hours !== undefined && row.hours !== null
        ? Number(row.hours || 0)
        : round2(Number(row.quantity ?? row.qty ?? 0));

    const hourlyRate = getRowUnitPrice(row);

    return {
      ...row,
      quantity: hours,
      hours,
      hourlyRate,
      rate: hourlyRate,
      unitPrice: hourlyRate,
      total: round2(hours * hourlyRate),
      flags: {
        ...(row.flags || {}),
        manuallyEdited: isManualRow(row),
        aiAdjusted: isAiAdjusted(row),
        reviewRequired: isReviewRow(row),
      },
      manuallyEdited: isManualRow(row),
      aiAdjusted: isAiAdjusted(row),
      meta: normalizeRowMeta(row, "labour", index),
    };
  });

  const extras = (normalized.extras || []).map((row, index) => {
    const qty = getRowQuantity(row);
    const price = getRowUnitPrice(row);

    return {
      ...row,
      quantity: qty,
      qty,
      price,
      unitPrice: price,
      total: round2(qty * price),
      name: row?.name || row?.label || "Untitled extra",
      flags: {
        ...(row.flags || {}),
        manuallyEdited: isManualRow(row),
        aiAdjusted: isAiAdjusted(row),
        reviewRequired: isReviewRow(row),
      },
      manuallyEdited: isManualRow(row),
      aiAdjusted: isAiAdjusted(row),
      meta: normalizeRowMeta(row, "extra", index),
    };
  });

  const summary = calculateEstimateTotals({
    ...normalized,
    materials,
    labour,
    extras,
    meta: {
      ...(normalized?.meta || {}),
      marginPercent: Number(normalized?.meta?.marginPercent ?? 25),
    },
  });

  return {
    ...normalized,
    meta: {
      ...(normalized?.meta || {}),
      marginPercent: summary.marginPercent,
    },
    materials,
    labour,
    extras,
    summary,
  };
}